/**
 * Unify CareTeam model.
 *
 * CareTeam is the **access compartment boundary** in Unify's RBAC model.
 * Every patient belongs to exactly one CareTeam, and AccessPolicies scope
 * provider/supervisor visibility to resources within their CareTeam
 * compartment (`_compartment=%care_team`).
 *
 * The `$set-accounts` operation places a Patient into a CareTeam compartment
 * by setting `Patient.meta.account` to the CareTeam reference.
 *
 * ┌──────────────────────┬───────────────────────────────────────────────────────────┐
 * │ Field                │ Description                                               │
 * ├──────────────────────┼───────────────────────────────────────────────────────────┤
 * │ name                 │ Human-readable panel name (e.g. "Provider A Panel")       │
 * │ status               │ "active" | "inactive" | "entered-in-error"                │
 * │ subject              │ Reference<Patient> — the patient this team manages        │
 * │ participant[]        │ Array of team members with roles                          │
 * │  .role[]             │ ParticipationType coding (PPRF, SPRF)                     │
 * │  .member             │ Reference<Practitioner>                                   │
 * │ managingOrganization │ Reference<Organization>[] — the practice                  │
 * └──────────────────────┴───────────────────────────────────────────────────────────┘
 *
 * Participant roles (http://terminology.hl7.org/CodeSystem/v3-ParticipationType):
 *   - PPRF = Primary Performer (treating provider)
 *   - SPRF = Secondary Performer (supervisor)
 *
 * Extension summary:
 *   (none — CareTeam uses standard FHIR fields only)
 */
import type { CareTeam, CareTeamParticipant, Reference } from '@medplum/fhirtypes';

// ═══════════════════════════════════════════════════════════════════════
//  Literal union types
// ═══════════════════════════════════════════════════════════════════════

/** CareTeam statuses used by Unify. */
export type CareTeamStatus = 'active' | 'inactive' | 'entered-in-error';

/** Participation role codes from v3-ParticipationType. */
export type ParticipationRoleCode = 'PPRF' | 'SPRF';

// ═══════════════════════════════════════════════════════════════════════
//  Getter helpers
// ═══════════════════════════════════════════════════════════════════════

/** Returns the first managingOrganization reference. */
export function getManagingOrganizationRef(resource: CareTeam): Reference | undefined {
  return resource.managingOrganization?.[0];
}

/** Returns the subject (Patient) reference. */
export function getCareTeamPatientRef(resource: CareTeam): Reference | undefined {
  return resource.subject;
}

/** Returns all Practitioner member references from participants. */
export function getPractitionerParticipantRefs(resource: CareTeam): Reference[] {
  const refs: Reference[] = [];
  for (const p of resource.participant ?? []) {
    if (p.member?.reference?.startsWith('Practitioner/')) {
      refs.push(p.member);
    }
  }
  return refs;
}

/** Returns the participant entry matching a given role code. */
export function getParticipantByRole(
  resource: CareTeam,
  role: ParticipationRoleCode,
): CareTeamParticipant | undefined {
  return resource.participant?.find(
    (p) => p.role?.some(
      (r) => r.coding?.some(
        (c) => c.system === 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType' && c.code === role,
      ),
    ),
  );
}

/** Returns the primary performer (treating provider) reference. */
export function getPrimaryPerformerRef(resource: CareTeam): Reference | undefined {
  return getParticipantByRole(resource, 'PPRF')?.member;
}

/** Returns the secondary performer (supervisor) reference. */
export function getSupervisorParticipantRef(resource: CareTeam): Reference | undefined {
  return getParticipantByRole(resource, 'SPRF')?.member;
}

// ═══════════════════════════════════════════════════════════════════════
//  Builder helpers
// ═══════════════════════════════════════════════════════════════════════

const PARTICIPATION_TYPE_SYSTEM = 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType';

export function buildParticipant(
  memberRef: Reference,
  role: ParticipationRoleCode,
): CareTeamParticipant {
  const display = role === 'PPRF' ? 'Primary Performer' : 'Secondary Performer';
  return {
    role: [{ coding: [{ system: PARTICIPATION_TYPE_SYSTEM, code: role, display }] }],
    // Callers pass Practitioner refs; param stays loose for server call sites.
    member: memberRef as CareTeamParticipant['member'],
  };
}

/** True when the CareTeam already has a participant whose `member.reference` matches `ref`. */
export function hasParticipantRef(resource: CareTeam, ref: string): boolean {
  return resource.participant?.some((p) => p.member?.reference === ref) ?? false;
}

/**
 * Add a participant to the CareTeam if not already present.
 * Returns a shallow copy; does NOT mutate the original.
 */
export function addParticipantIfMissing(
  resource: CareTeam,
  memberRef: Reference,
  role: ParticipationRoleCode,
): CareTeam {
  const ref = memberRef.reference;
  if (!ref || hasParticipantRef(resource, ref)) return resource;
  return {
    ...resource,
    participant: [...(resource.participant ?? []), buildParticipant(memberRef, role)],
  };
}

function participantHasRole(participant: CareTeamParticipant, role: ParticipationRoleCode): boolean {
  return (
    participant.role?.some((r) =>
      r.coding?.some(
        (c) => c.system === PARTICIPATION_TYPE_SYSTEM && c.code === role,
      ),
    ) ?? false
  );
}

/** Remove all participants with the given role. Returns a shallow copy. */
export function removeParticipantsByRole(
  resource: CareTeam,
  role: ParticipationRoleCode,
): CareTeam {
  const remaining = (resource.participant ?? []).filter((p) => !participantHasRole(p, role));
  if (remaining.length === (resource.participant ?? []).length) return resource;
  return {
    ...resource,
    participant: remaining.length > 0 ? remaining : undefined,
  };
}

/** Replace the SPRF participant to match the provider's current supervisor (or clear it). */
export function setSupervisorParticipant(
  resource: CareTeam,
  supervisorRef: Reference | undefined,
): CareTeam {
  const withoutSupervisor = removeParticipantsByRole(resource, 'SPRF');
  if (!supervisorRef?.reference) return withoutSupervisor;
  return addParticipantIfMissing(withoutSupervisor, supervisorRef, 'SPRF');
}

function isCurrentParticipant(p: CareTeamParticipant): boolean {
  return !p.period?.end;
}

/** Active panel where the practitioner is the primary performer (PPRF). */
export function isProviderPrimaryPanel(careTeam: CareTeam, practitionerId: string): boolean {
  const pprf = getParticipantByRole(careTeam, 'PPRF');
  return (
    !!pprf &&
    isCurrentParticipant(pprf) &&
    pprf.member?.reference === `Practitioner/${practitionerId}`
  );
}
